import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { encodeUrlParams } from 'common/services/utilService'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'
import { parseJsonString, processDrawingsData } from 'features/Procedure/utils/api.utils'
import {
  hasPatientEncounter,
  markEncounterFormSigned,
} from 'features/Procedure/state/e-sign-patients.helpers'
import {
  filterStillUploadingSoapImages,
  mergeSoapNotesPreservingPendingImages,
  normalizeSoapImage,
  normalizeSoapNotes,
  soapImageBlobToDataUrl,
} from './soapImageUpload.helpers'
import { getSaveSoapNoteInvalidatedTags } from './soapNote.helpers'
import type {
  CreateProcedureDto,
  CreateProductDto,
  DeleteProcedureDto,
  DeleteSoapNoteDto,
  DeleteSoapImageDto,
  EmrCodeDetails,
  EmrCodeDetailsResponse,
  EmrCodeItem,
  EmrCodesDto,
  EmrCodesResponse,
  EmrScanProductDto,
  EmrScanProductResponse,
  EncounterLabApiItem,
  EncounterPatientsResponse,
  EncounterPhotoApiItem,
  EncounterPhotosApiResponse,
  ESignFormStatusData,
  FetchDocTemplateDto,
  FetchDocTemplateResponse,
  FetchEmrCodeDetailsDto,
  FetchEncounterCheckoutDto,
  EncounterCheckoutData,
  FetchIntakeTemplatesDto,
  FetchIntakeFormsDto,
  IntakeFormsData,
  IntakeFormsResponse,
  UploadPatientProfileImageDto,
  UploadPatientProfileImageResponse,
  FetchSignedDocumentsDto,
  FetchESignFormDto,
  FetchESignFormResponse,
  FetchEncounterLabsDto,
  FetchEncounterPatientsDto,
  FetchEncounterPhotosDto,
  FetchPhotoStreamDto,
  FetchProceduresDto,
  FetchProductsDto,
  FetchSoapImageDto,
  FetchSoapImagesDto,
  FetchSoapNotesDto,
  PrescriptionApiItem,
  PrescriptionsApiResponse,
  ProcedureItem,
  ProcedureMutationResponse,
  ProceduresResponse,
  ProductsResponse,
  SaveSoapNoteDto,
  SoapImageApiItem,
  SoapImageMutationResponse,
  SoapImagesApiResponse,
  SoapNoteApiItem,
  SoapNoteMutationResponse,
  SoapNotesApiResponse,
  SubmitDocTemplateDto,
  SubmitDocTemplateResponse,
  SubmitESignFormDto,
  SubmitESignFormResponse,
  UpdateEncounterPhotosDto,
  UpdateEncounterPhotosResponse,
  UpdateProcedureDto,
  UpdateProductDto,
  UpdatePatientContactDto,
  UpdatePatientContactResponse,
  CreateReferralSourceDto,
  CreateReferralSourceResponse,
  PhysicianIntroduction,
  FetchPhysicianIntroductionsDto,
  PhysicianIntroductionsResponse,
  CreatePhysicianIntroductionDto,
  CreatePhysicianIntroductionResponse,
  IntakeTemplateItem,
  IntakeTemplatesApiResponse,
  EncounterCheckoutApiResponse,
  SignedDocumentApiItem,
  SignedDocumentsApiResponse,
  UploadEncounterPhotoDto,
  UploadEncounterPhotoResponse,
  FetchStripeTerminalsDto,
  FetchStripeTerminalsResponse,
  PayEncounterDto,
  PayEncounterResponse,
  CancelEncounterPayDto,
  CancelEncounterPayResponse,
  SaveCreditCardDto,
  SaveCreditCardResponse,
  StripeTerminal,
} from './procedure-api.type'
import { transformEncounterLabsResponse } from './encounter-labs.transform'
import { transformIntakeFormsResponse } from './intake-forms.transform'
import {
  mergeProcedureDrawingElements,
  removeProcedureDrawingElements,
  setProcedureDeletePending,
  setSelectedProcedure,
  setDrawingElements,
} from './procedure.slice'
import { DrawingElementVisibleState, ProcedureStoreTagTypes } from './procedure.type'

let nextTempSoapEntityId = -1

const getNextTempSoapEntityId = () => {
  const tempId = nextTempSoapEntityId
  nextTempSoapEntityId -= 1
  return tempId
}

const emptyEncounterCheckoutData: EncounterCheckoutData = {
  charges: [],
  applied_discounts: [],
  summary: {
    total_charges: 0,
    total_tax: 0,
    total_with_tax: 0,
    total_adjustments: 0,
    prev_payments: 0,
    prev_credits: 0,
    prev_gc: 0,
    total_payments: 0,
    balance: 0,
    has_partial_payment: false,
    encounter_is_billed: false,
    tax_code_text: '',
  },
  discounts: [],
  credits: [],
  patient_default_discount: null,
  credit_card: null,
  stripe_key: '',
}

const normalizeEncounterCheckoutData = (
  data: EncounterCheckoutData | null | undefined,
): EncounterCheckoutData => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return emptyEncounterCheckoutData
  }

  return {
    ...emptyEncounterCheckoutData,
    ...data,
    charges: Array.isArray(data.charges) ? data.charges : [],
    applied_discounts: Array.isArray(data.applied_discounts) ? data.applied_discounts : [],
    discounts: Array.isArray(data.discounts) ? data.discounts : [],
    credits: Array.isArray(data.credits) ? data.credits : [],
    summary: data.summary ?? emptyEncounterCheckoutData.summary,
  }
}

const createOptimisticSoapNote = (patientPid: number): SoapNoteApiItem => ({
  id: getNextTempSoapEntityId(),
  date: new Date().toISOString(),
  pid: patientPid,
  user: null,
  groupname: null,
  authorized: 1,
  activity: 1,
  subjective: null,
  objective: null,
  assessment: null,
  plan: null,
  soap_images: [],
})

const hasSoapNoteContent = (note: SoapNoteApiItem) =>
  Boolean(note.subjective || note.objective || note.assessment || note.plan)

const buildOptimisticSoapNotes = ({
  currentNotes,
  patientPid,
  tempImage,
}: {
  currentNotes: SoapNoteApiItem[] | undefined
  patientPid: number
  tempImage: SoapImageApiItem
}): SoapNoteApiItem[] => {
  const nextNotes =
    currentNotes?.map((note) => ({
      ...note,
      soap_images: Array.isArray(note.soap_images) ? [...note.soap_images] : [],
    })) || []

  if (nextNotes.length === 0) {
    nextNotes.unshift(createOptimisticSoapNote(patientPid))
  }

  if (!Array.isArray(nextNotes[0].soap_images)) {
    nextNotes[0].soap_images = []
  }

  nextNotes[0].soap_images.push(tempImage)

  return nextNotes
}

const normalizeProcedureItem = (procedure: ProcedureItem): ProcedureItem => ({
  ...procedure,
  product_labels: parseJsonString<string[]>(procedure.product_labels as any) || [],
})

export const procedureApi = createApi({
  reducerPath: REDUCERS.PROCEDURE,
  baseQuery: baseQuery(),
  tagTypes: [
    ProcedureStoreTagTypes.Procedures,
    ProcedureStoreTagTypes.ProcedureCodes,
    ProcedureStoreTagTypes.Products,
    ProcedureStoreTagTypes.SoapNotes,
    ProcedureStoreTagTypes.EncounterPhotos,
    ProcedureStoreTagTypes.SoapImages,
    ProcedureStoreTagTypes.ESignForm,
    ProcedureStoreTagTypes.SignedDocuments,
    ProcedureStoreTagTypes.IntakeTemplates,
    ProcedureStoreTagTypes.IntakeForms,
    ProcedureStoreTagTypes.EncounterCheckout,
    ProcedureStoreTagTypes.StripeTerminals,
    ProcedureStoreTagTypes.PhysicianIntroductions,
  ],
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchEmrCodes: build.query<EmrCodeItem[], EmrCodesDto>({
      query: ({ siteId, search, code_type, category }) => {
        const params: Record<string, string> = { search, code_type }
        if (category) {
          params.category = category
        }
        return {
          url: `/emr/open-emr-codes${encodeUrlParams(params)}`,
          headers: { 'Database-Connection': siteId },
        }
      },
      transformResponse: (response: EmrCodesResponse): EmrCodeItem[] => response.data,
      providesTags: [ProcedureStoreTagTypes.ProcedureCodes],
    }),
    fetchEmrCodeDetails: build.query<EmrCodeDetails, FetchEmrCodeDetailsDto>({
      query: ({ siteId, codeId }) => ({
        url: `/emr/open-emr-codes/${codeId}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: EmrCodeDetailsResponse): EmrCodeDetails => response.data,
      providesTags: [ProcedureStoreTagTypes.ProcedureCodes],
    }),
    fetchEmrScanProduct: build.query<EmrScanProductResponse, EmrScanProductDto>({
      query: ({ siteId, search }) => ({
        url: `/emr/open-emr-scan-product${encodeUrlParams({ search })}`,
        headers: { 'Database-Connection': siteId },
      }),
      extraOptions: { maxRetries: 0 },
    }),
    fetchProcedures: build.query<ProceduresResponse, FetchProceduresDto>({
      query: ({ siteId, encounterUid }) => ({
        url: `/emr/encounter/${encounterUid}/procedures?procedure_history=1`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: unknown): ProceduresResponse => {
        const body = (response ?? {}) as Record<string, unknown>
        const inner = ((body.data ?? body) as Record<string, unknown>) || {}
        const rawItems = Array.isArray(inner.items) ? (inner.items as ProcedureItem[]) : []
        return {
          ...(inner as object),
          items: rawItems.map((item) => ({
            ...item,
            product_labels: parseJsonString<string[]>(item.product_labels as any) || [],
          })),
          historical_procedures: (body.historical_procedures as any) || {
            encounter_count: 0,
            items: [],
            historical_documents: [],
          },
          discount_categories: body.discount_categories as any,
          discount_type: body.discount_type as any,
          patient_default_discount: body.patient_default_discount as any,
        } as ProceduresResponse
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState() as {
            procedureReducer?: {
              selectedEncounterUid?: string | null
            }
          }

          if (state.procedureReducer?.selectedEncounterUid !== arg.encounterUid) {
            return
          }

          const drawings = processDrawingsData(data)
          const encounterProcedureIds = (data?.items || []).map((item) => item.id)
          dispatch(mergeProcedureDrawingElements({ drawings, encounterProcedureIds }))
        } catch {
          // do nothing
        }
      },
      providesTags: [ProcedureStoreTagTypes.Procedures],
    }),
    fetchProducts: build.query<ProductsResponse, FetchProductsDto>({
      query: ({ siteId, encounterUid }) => ({
        url: `/emr/encounter/${encounterUid}/procedures?products=1`,
        headers: { 'Database-Connection': siteId },
      }),
      providesTags: [ProcedureStoreTagTypes.Products],
    }),
    createProcedure: build.mutation<ProcedureMutationResponse, CreateProcedureDto>({
      query: ({ siteId, encounterUid, data }) => ({
        url: `/emr/encounter/${encounterUid}/procedures`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data,
      }),
      onQueryStarted: async ({ siteId, encounterUid }, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState() as {
            procedureReducer?: {
              selectedEncounterUid?: string | null
            }
          }

          if (state.procedureReducer?.selectedEncounterUid !== encounterUid) {
            return
          }

          const addedProcedures = Array.isArray(data.added_procedures)
            ? data.added_procedures.map(normalizeProcedureItem)
            : []

          if (addedProcedures.length === 0) {
            return
          }

          dispatch(
            procedureApi.util.updateQueryData(
              'fetchProcedures',
              { siteId, encounterUid },
              (draft) => {
                const nextItems = [...(draft.items || [])]

                addedProcedures.forEach((procedure) => {
                  const existingIndex = nextItems.findIndex((item) => item.id === procedure.id)

                  if (existingIndex === -1) {
                    nextItems.push(procedure)
                    return
                  }

                  nextItems[existingIndex] = {
                    ...nextItems[existingIndex],
                    ...procedure,
                  }
                })

                draft.items = nextItems
              },
            ),
          )
        } catch {
          // do nothing
        }
      },
      invalidatesTags: [
        ProcedureStoreTagTypes.Procedures,
        ProcedureStoreTagTypes.ESignForm,
        ProcedureStoreTagTypes.EncounterCheckout,
        ProcedureStoreTagTypes.SoapNotes,
      ],
    }),
    updateProcedure: build.mutation<ProcedureMutationResponse, UpdateProcedureDto>({
      query: ({ siteId, encounterUid, data }) => {
        return {
          url: `/emr/encounter/${encounterUid}/procedures`,
          method: HTTP_METHODS.PUT,
          headers: { 'Database-Connection': siteId },
          data,
        }
      },
      onQueryStarted: async (
        { siteId, encounterUid, data },
        { dispatch, queryFulfilled, getState },
      ) => {
        const patchResult = dispatch(
          procedureApi.util.updateQueryData(
            'fetchProcedures',
            { siteId, encounterUid },
            (draft) => {
              if (data.model_version) {
                draft.model_version = data.model_version
              }
              if (data.general_note !== undefined) {
                draft.general_note = data.general_note
              }
              data.procedures.forEach((updatedProcedure) => {
                if (updatedProcedure.id) {
                  const items = draft.items || []
                  if (items) {
                    const index = items.findIndex((item) => item.id === updatedProcedure.id)
                    if (index !== -1) {
                      const current = items[index]
                      items[index] = {
                        ...current,
                        code: updatedProcedure.code,
                        code_type: updatedProcedure.code_type,
                        procedure_units: updatedProcedure.procedure_units,
                        units: updatedProcedure.units,
                        fee: updatedProcedure.fee,
                        provider_id: updatedProcedure.provider_id,
                        justify_codes: updatedProcedure.justify_codes,
                        product_labels:
                          parseJsonString<string[]>(updatedProcedure.product_labels as any) ||
                          current.product_labels ||
                          [],
                        note:
                          updatedProcedure.note !== undefined
                            ? updatedProcedure.note
                            : current.note,
                        drawings:
                          updatedProcedure.drawings !== undefined
                            ? {
                                id: current.drawings?.id ?? 0,
                                index: current.drawings?.index ?? null,
                                created_at: current.drawings?.created_at ?? '',
                                updated_at: current.drawings?.updated_at ?? '',
                                drawingElements: updatedProcedure.drawings,
                              }
                            : current.drawings,
                      }
                    }
                  }
                }
              })
            },
          ),
        )
        try {
          await queryFulfilled

          const state = getState() as any
          if (state.procedureReducer?.selectedEncounterUid !== encounterUid) {
            return
          }

          const drawingElements = state.procedureReducer?.drawingElements || []
          const savedDrawingIds = new Set<string>()

          data.procedures.forEach((proc) => {
            if (proc.drawings) {
              proc.drawings.forEach((drawing) => {
                if (drawing.id) {
                  savedDrawingIds.add(drawing.id)
                }
              })
            }
          })

          const updatedDrawingElements = drawingElements
            .filter((el: any) => el.visibleState !== DrawingElementVisibleState.HIDDEN)
            .map((el: any) => {
              if (savedDrawingIds.has(el.id)) {
                return {
                  ...el,
                  isModified: false,
                }
              }
              return el
            })

          dispatch(setDrawingElements(updatedDrawingElements))
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: (_result, _error, { skipESignInvalidation }) =>
        skipESignInvalidation
          ? [ProcedureStoreTagTypes.SoapNotes]
          : [
              ProcedureStoreTagTypes.SoapNotes,
              ProcedureStoreTagTypes.ESignForm,
              ProcedureStoreTagTypes.EncounterCheckout,
            ],
    }),

    createProduct: build.mutation<ProcedureMutationResponse, CreateProductDto>({
      query: ({ siteId, encounterUid, data }) => ({
        url: `/emr/encounter/${encounterUid}/procedures?products=1`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data,
      }),
      invalidatesTags: [
        ProcedureStoreTagTypes.Products,
        ProcedureStoreTagTypes.ESignForm,
        ProcedureStoreTagTypes.EncounterCheckout,
      ],
    }),
    updateProduct: build.mutation<ProcedureMutationResponse, UpdateProductDto>({
      query: ({ siteId, encounterUid, data }) => ({
        url: `/emr/encounter/${encounterUid}/procedures?products=1`,
        method: HTTP_METHODS.PUT,
        headers: { 'Database-Connection': siteId },
        data,
      }),
      invalidatesTags: [
        ProcedureStoreTagTypes.Products,
        ProcedureStoreTagTypes.ESignForm,
        ProcedureStoreTagTypes.EncounterCheckout,
      ],
    }),
    deleteProcedure: build.mutation<ProcedureMutationResponse, DeleteProcedureDto>({
      query: ({ siteId, encounterUid, procedureId }) => ({
        url: `/emr/encounter/${encounterUid}/procedures/${procedureId}`,
        method: HTTP_METHODS.DELETE,
        headers: { 'Database-Connection': siteId },
      }),
      onQueryStarted: async (
        { siteId, encounterUid, procedureId },
        { dispatch, queryFulfilled, getState },
      ) => {
        const initialState = getState() as {
          procedureReducer?: {
            selectedProcedure?: ProcedureItem | null
          }
        }
        const previousSelectedProcedure = initialState.procedureReducer?.selectedProcedure ?? null
        const wasDeletingSelectedProcedure = previousSelectedProcedure?.id === procedureId

        dispatch(setProcedureDeletePending(procedureId))

        if (wasDeletingSelectedProcedure) {
          dispatch(setSelectedProcedure(null))
        }

        try {
          await queryFulfilled

          dispatch(
            procedureApi.util.updateQueryData(
              'fetchProcedures',
              { siteId, encounterUid },
              (draft) => {
                draft.items = (draft.items || []).filter((item) => item.id !== procedureId)
              },
            ),
          )

          dispatch(removeProcedureDrawingElements(procedureId))
        } catch {
          if (wasDeletingSelectedProcedure && previousSelectedProcedure) {
            dispatch(setSelectedProcedure(previousSelectedProcedure))
          }
        } finally {
          dispatch(setProcedureDeletePending(null))
        }
      },
      invalidatesTags: [
        ProcedureStoreTagTypes.Procedures,
        ProcedureStoreTagTypes.Products,
        ProcedureStoreTagTypes.ESignForm,
        ProcedureStoreTagTypes.EncounterCheckout,
      ],
    }),
    fetchEncounterPatients: build.query<EncounterPatientsResponse, FetchEncounterPatientsDto>({
      query: ({ siteId }) => ({
        url: '/emr/encounter/patients',
        headers: { 'Database-Connection': siteId },
      }),
    }),
    updatePatientContact: build.mutation<UpdatePatientContactResponse, UpdatePatientContactDto>({
      query: ({ patientId, data }) => ({
        url: `/patients/${patientId}/contact`,
        method: HTTP_METHODS.PUT,
        data,
      }),
      onQueryStarted: async ({ siteId, patientId, data }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          procedureApi.util.updateQueryData('fetchEncounterPatients', { siteId }, (draft) => {
            const entry = draft.patients.find((item) => item.patient.patient_id === patientId)
            if (entry) {
              entry.patient = { ...entry.patient, ...data }
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    fetchPhysicianIntroductions: build.query<
      PhysicianIntroduction[],
      FetchPhysicianIntroductionsDto
    >({
      query: ({ siteId, patientId }) => ({
        url: `/emr/patient/${patientId}/physician-introductions`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: PhysicianIntroductionsResponse): PhysicianIntroduction[] =>
        response.physicians ?? [],
      providesTags: [ProcedureStoreTagTypes.PhysicianIntroductions],
    }),
    createPhysicianIntroduction: build.mutation<
      CreatePhysicianIntroductionResponse,
      CreatePhysicianIntroductionDto
    >({
      query: ({ siteId, patientId, physicianId, introducedAt }) => ({
        url: `/emr/patient/${patientId}/physician-introductions`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: { physician_id: physicianId, introduced_at: introducedAt },
      }),
      onQueryStarted: async (
        { siteId, patientId, physicianId, introducedAt },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          procedureApi.util.updateQueryData(
            'fetchPhysicianIntroductions',
            { siteId, patientId },
            (draft) => {
              const entry = draft.find((item) => item.user_id === physicianId)
              if (entry) {
                entry.introduced = true
                entry.provider_since = introducedAt.slice(0, 10)
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: [ProcedureStoreTagTypes.PhysicianIntroductions],
    }),
    createReferralSource: build.mutation<CreateReferralSourceResponse, CreateReferralSourceDto>({
      query: ({ siteId, title }) => ({
        url: '/emr/referral_source',
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: { title },
      }),
      onQueryStarted: async ({ siteId, title }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          if (data?.status === false || data?.success === false) {
            return
          }
          dispatch(
            procedureApi.util.updateQueryData('fetchEncounterPatients', { siteId }, (draft) => {
              const nextSources = { ...(draft.referral_sources ?? {}) }
              nextSources[title] = title
              draft.referral_sources = nextSources
            }),
          )
        } catch {
          // do nothing
        }
      },
    }),
    fetchEncounterPhotos: build.query<EncounterPhotoApiItem[], FetchEncounterPhotosDto>({
      query: ({ siteId, patientId, encounterUid }) => ({
        url: `/emr/encounter-photo/${patientId}/${encounterUid}`,
        headers: { 'Database-Connection': siteId },
        requireSuccessTrue: true,
      }),
      transformResponse: (response: EncounterPhotosApiResponse): EncounterPhotoApiItem[] =>
        response?.data ?? [],
      providesTags: [ProcedureStoreTagTypes.EncounterPhotos],
    }),
    fetchPhotoStream: build.query<Blob, FetchPhotoStreamDto>({
      query: ({ siteId, patientId, documentId }) => ({
        url: `/emr/patient-document-stream/${patientId}/${documentId}`,
        headers: { 'Database-Connection': siteId },
        responseType: 'blob',
      }),
    }),
    fetchEncounterLabs: build.query<EncounterLabApiItem[], FetchEncounterLabsDto>({
      query: ({ siteId, patientId, encounterUid }) => ({
        url: `/emr/encounter-labs/${patientId}/${encounterUid}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: transformEncounterLabsResponse,
    }),
    uploadEncounterPhoto: build.mutation<UploadEncounterPhotoResponse, UploadEncounterPhotoDto>({
      query: ({ siteId, patientId, encounterUid, formData }) => ({
        url: `/emr/encounter-photo/${patientId}/${encounterUid}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: formData,
      }),
      extraOptions: {
        maxRetries: 2,
      },
    }),
    updateEncounterPhotos: build.mutation<UpdateEncounterPhotosResponse, UpdateEncounterPhotosDto>({
      query: ({ siteId, patientId, encounterUid, files }) => ({
        url: `/emr/encounter-photo/${patientId}/${encounterUid}`,
        method: HTTP_METHODS.PUT,
        headers: { 'Database-Connection': siteId },
        data: { files },
      }),
    }),
    fetchPrescriptions: build.query<PrescriptionApiItem[], { patientId: number }>({
      query: ({ patientId }) => ({
        url: `/open-emr-prescriptions/from-openemr/${patientId}`,
      }),
      transformResponse: (response: PrescriptionsApiResponse): PrescriptionApiItem[] =>
        response.prescriptions,
      providesTags: [ProcedureStoreTagTypes.Procedures],
    }),
    fetchSoapNotes: build.query<SoapNoteApiItem[], FetchSoapNotesDto>({
      query: ({ patientPid, encounterUid }) => ({
        url: `/emr/encounter-soap-notes/${patientPid}/${encounterUid}`,
        requireSuccessTrue: true,
      }),
      transformResponse: (response: SoapNotesApiResponse): SoapNoteApiItem[] =>
        normalizeSoapNotes(response.data ?? []),
      merge: (currentCacheData, responseData) =>
        mergeSoapNotesPreservingPendingImages(currentCacheData, responseData),
      providesTags: [ProcedureStoreTagTypes.SoapNotes],
    }),
    saveSoapNote: build.mutation<SoapNoteMutationResponse, SaveSoapNoteDto>({
      query: ({ patientPid, encounterUid, payload }) => ({
        url: `/emr/encounter-soap-notes/${patientPid}/${encounterUid}`,
        method: HTTP_METHODS.POST,
        data: payload,
        requireSuccessTrue: true,
      }),
      async onQueryStarted({ patientPid, encounterUid, payload }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const resolvedNoteId = Number(data.data?.id ?? data.data?.sid)
          const hasResolvedNoteId = Number.isFinite(resolvedNoteId) && resolvedNoteId > 0

          dispatch(
            procedureApi.util.updateQueryData(
              'fetchSoapNotes',
              { patientPid, encounterUid },
              (draft) => {
                if (!draft) {
                  return
                }

                const existingNote =
                  (hasResolvedNoteId
                    ? draft.find((note) => note.id === resolvedNoteId)
                    : undefined) ?? draft[0]

                if (existingNote) {
                  if (hasResolvedNoteId) {
                    existingNote.id = resolvedNoteId
                  }
                  if (typeof data.data?.date === 'string') {
                    existingNote.date = data.data.date
                  }
                  if (data.data?.user !== undefined) {
                    existingNote.user = data.data.user ?? null
                  }
                  if (data.data?.groupname !== undefined) {
                    existingNote.groupname = data.data.groupname ?? null
                  }
                  if (typeof data.data?.authorized === 'number') {
                    existingNote.authorized = data.data.authorized
                  }
                  if (typeof data.data?.activity === 'number') {
                    existingNote.activity = data.data.activity
                  }
                  if (payload.subjective !== undefined) {
                    existingNote.subjective = data.data?.subjective ?? payload.subjective
                  }
                  if (payload.objective !== undefined) {
                    existingNote.objective = data.data?.objective ?? payload.objective
                  }
                  if (payload.assessment !== undefined) {
                    existingNote.assessment = data.data?.assessment ?? payload.assessment
                  }
                  if (data.data?.plan !== undefined) {
                    existingNote.plan = data.data.plan ?? null
                  }
                  if (Array.isArray(data.data?.soap_images)) {
                    const pendingImages = filterStillUploadingSoapImages(
                      existingNote.soap_images || [],
                    )
                    existingNote.soap_images = [
                      ...data.data.soap_images.map(normalizeSoapImage),
                      ...pendingImages,
                    ]
                  }
                  return
                }

                if (!hasResolvedNoteId) {
                  return
                }

                draft.unshift({
                  id: resolvedNoteId,
                  date:
                    typeof data.data?.date === 'string' ? data.data.date : new Date().toISOString(),
                  pid: patientPid,
                  user: data.data?.user ?? null,
                  groupname: data.data?.groupname ?? null,
                  authorized: typeof data.data?.authorized === 'number' ? data.data.authorized : 1,
                  activity: typeof data.data?.activity === 'number' ? data.data.activity : 1,
                  subjective: data.data?.subjective ?? payload.subjective ?? null,
                  objective: data.data?.objective ?? payload.objective ?? null,
                  assessment: data.data?.assessment ?? payload.assessment ?? null,
                  plan: data.data?.plan ?? null,
                  soap_images: Array.isArray(data.data?.soap_images)
                    ? data.data.soap_images.map(normalizeSoapImage)
                    : [],
                })
              },
            ),
          )
        } catch {
          // do nothing
        }
      },
      // Autosave retries itself; baseQuery POST retries hold the save lock and can duplicate the note.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_result, error, args) => getSaveSoapNoteInvalidatedTags(error, args),
    }),
    deleteSoapNote: build.mutation<SoapNoteMutationResponse, DeleteSoapNoteDto>({
      query: ({ patientPid, encounterUid, noteId }) => ({
        url: `/emr/encounter-soap-notes/${patientPid}/${encounterUid}/${noteId}`,
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: [ProcedureStoreTagTypes.SoapNotes, ProcedureStoreTagTypes.ESignForm],
    }),
    fetchSoapImages: build.query<SoapImageApiItem[], FetchSoapImagesDto>({
      query: ({ patientPid }) => ({
        url: `/emr/encounter-soap-image/${patientPid}`,
      }),
      transformResponse: (response: SoapImagesApiResponse): SoapImageApiItem[] =>
        response?.data ?? [],
      providesTags: [ProcedureStoreTagTypes.SoapImages],
    }),

    // BE returns the full-resolution image as raw binary (image/jpeg).
    fetchSoapImage: build.query<string | null, FetchSoapImageDto>({
      query: ({ patientPid, encounterUid, imageId }) => ({
        url: `/emr/encounter-soap-image/${patientPid}/${encounterUid}/${imageId}`,
        responseType: 'blob',
      }),
      transformResponse: async (response: unknown): Promise<string | null> => {
        if (response instanceof Blob && response.type.startsWith('image/')) {
          return soapImageBlobToDataUrl(response)
        }
        if (typeof response === 'string' && response.startsWith('data:image/')) {
          return response
        }
        return null
      },
    }),

    deleteSoapImage: build.mutation<SoapImageMutationResponse, DeleteSoapImageDto>({
      query: ({ patientPid, encounterUid, imageId }) => ({
        url: `/emr/encounter-soap-image/${patientPid}/${encounterUid}/${imageId}`,
        method: HTTP_METHODS.DELETE,
      }),
      onQueryStarted: async (
        { patientPid, encounterUid, imageId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          procedureApi.util.updateQueryData(
            'fetchSoapNotes',
            { patientPid, encounterUid },
            (draft) => {
              if (draft) {
                draft.forEach((note) => {
                  if (note.soap_images) {
                    note.soap_images = note.soap_images.filter((img) => img.id !== imageId)
                  }
                })
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: [ProcedureStoreTagTypes.SoapImages],
    }),
    fetchESignForm: build.query<ESignFormStatusData, FetchESignFormDto>({
      query: ({ siteId, patientId, encounterUid }) => ({
        url: `/emr/esign-form/${patientId}/${encounterUid}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: FetchESignFormResponse): ESignFormStatusData =>
        response?.data ?? {},
      providesTags: [ProcedureStoreTagTypes.ESignForm],
    }),
    submitESignForm: build.mutation<SubmitESignFormResponse, SubmitESignFormDto>({
      query: ({ siteId, patientId, encounterUid, payload }) => ({
        url: `/emr/esign-form/${patientId}/${encounterUid}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: payload,
      }),
      onQueryStarted: async (
        { siteId, patientId, encounterUid, payload },
        { dispatch, getState, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled
          if (!data.success) return
        } catch {
          return
        }
        // A freshly created patient/encounter is not in the cached list yet; refetch it first.
        const cachedPatients =
          procedureApi.endpoints.fetchEncounterPatients.select({ siteId })(getState()).data
            ?.patients ?? []
        if (!hasPatientEncounter(cachedPatients, patientId, encounterUid)) {
          // Resolves after any in-flight list request too, so the patch below lands on fresh data.
          await dispatch(
            procedureApi.endpoints.fetchEncounterPatients.initiate(
              { siteId },
              { forceRefetch: true, subscribe: false },
            ),
          )
        }
        // Keep the patient selector e-sign chips in sync; a no-op if the pair is still absent.
        dispatch(
          procedureApi.util.updateQueryData('fetchEncounterPatients', { siteId }, (draft) => {
            draft.patients = markEncounterFormSigned(
              draft.patients,
              patientId,
              encounterUid,
              payload.form_name,
            )
          }),
        )
      },
      invalidatesTags: [ProcedureStoreTagTypes.ESignForm],
    }),
    fetchDocTemplate: build.query<FetchDocTemplateResponse, FetchDocTemplateDto>({
      query: ({ patientId, encounterUid, templateId }) => ({
        url: `/emr/forms/${patientId}/${encounterUid}/template/${templateId}`,
      }),
    }),
    submitDocTemplate: build.mutation<SubmitDocTemplateResponse, SubmitDocTemplateDto>({
      query: ({ patientId, templateId, encounterUid, payload }) => ({
        url: `/emr/forms/${patientId}/template/${templateId}/${encounterUid}`,
        method: HTTP_METHODS.POST,
        data: payload,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        ProcedureStoreTagTypes.Procedures,
        ProcedureStoreTagTypes.Products,
        ProcedureStoreTagTypes.SignedDocuments,
        ProcedureStoreTagTypes.IntakeTemplates,
        { type: ProcedureStoreTagTypes.IntakeForms, id: patientId },
      ],
    }),
    fetchSignedDocuments: build.query<SignedDocumentApiItem[], FetchSignedDocumentsDto>({
      query: ({ patientId }) => ({
        url: `/emr/forms/${patientId}/signed-documents`,
      }),
      transformResponse: (response: SignedDocumentsApiResponse): SignedDocumentApiItem[] =>
        Array.isArray(response?.data) ? response.data : [],
      providesTags: [ProcedureStoreTagTypes.SignedDocuments],
    }),
    fetchIntakeTemplates: build.query<IntakeTemplateItem[], FetchIntakeTemplatesDto>({
      query: ({ patientId }) => ({
        url: `/emr/forms/${patientId}/intake-templates`,
      }),
      transformResponse: (response: IntakeTemplatesApiResponse): IntakeTemplateItem[] => {
        if (!Array.isArray(response?.data)) return []
        return response.data.flatMap((group) => group.templates ?? [])
      },
      providesTags: [ProcedureStoreTagTypes.IntakeTemplates],
    }),
    fetchIntakeForms: build.query<IntakeFormsData, FetchIntakeFormsDto>({
      query: ({ siteId, patientId }) => ({
        url: `/emr/intake-form/${patientId}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: IntakeFormsResponse): IntakeFormsData =>
        transformIntakeFormsResponse(response),
      providesTags: (_result, _error, { patientId }) => [
        { type: ProcedureStoreTagTypes.IntakeForms, id: patientId },
      ],
    }),
    uploadPatientProfileImage: build.mutation<
      UploadPatientProfileImageResponse,
      UploadPatientProfileImageDto
    >({
      query: ({ siteId, patientId, formData }) => ({
        url: `/emr/patient/${patientId}/image-profile`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: formData,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: ProcedureStoreTagTypes.IntakeForms, id: patientId },
      ],
    }),
    fetchEncounterCheckout: build.query<EncounterCheckoutData, FetchEncounterCheckoutDto>({
      query: ({ siteId, patientId, encounterUid }) => ({
        url: `/emr/encounter-checkout/${patientId}/${encounterUid}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: EncounterCheckoutApiResponse): EncounterCheckoutData =>
        normalizeEncounterCheckoutData(response?.data),
      providesTags: [ProcedureStoreTagTypes.EncounterCheckout],
    }),
    fetchStripeTerminals: build.query<StripeTerminal[], FetchStripeTerminalsDto>({
      query: ({ siteId }) => ({
        url: '/emr/encounter-checkout/stripe-terminals',
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: FetchStripeTerminalsResponse): StripeTerminal[] =>
        Array.isArray(response?.terminals) ? response.terminals : [],
      providesTags: [ProcedureStoreTagTypes.StripeTerminals],
    }),
    payEncounter: build.mutation<PayEncounterResponse, PayEncounterDto>({
      query: ({ siteId, patientId, encounterUid, body }) => ({
        url: `/emr/encounter-pay/${patientId}/${encounterUid}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: body,
      }),
      extraOptions: { maxRetries: 0 },
    }),
    cancelEncounterPay: build.mutation<CancelEncounterPayResponse, CancelEncounterPayDto>({
      query: ({ siteId, patientId, encounterUid }) => ({
        url: `/emr/cancel-pay/${patientId}/${encounterUid}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
      }),
      extraOptions: { maxRetries: 0 },
    }),
    saveCreditCard: build.mutation<SaveCreditCardResponse, SaveCreditCardDto>({
      query: ({ siteId, patientId, body }) => ({
        url: `/emr/credit-card/${patientId}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: body,
      }),
      invalidatesTags: [ProcedureStoreTagTypes.EncounterCheckout],
    }),
  }),
})

export const {
  useFetchEmrCodesQuery,
  useLazyFetchEmrCodesQuery,
  useLazyFetchEmrCodeDetailsQuery,
  useLazyFetchEmrScanProductQuery,
  useFetchPrescriptionsQuery,
  useLazyFetchPrescriptionsQuery,
  useFetchProceduresQuery,
  useLazyFetchProceduresQuery,
  useFetchProductsQuery,
  useLazyFetchProductsQuery,
  useFetchEncounterPhotosQuery,
  useFetchPhotoStreamQuery,
  useFetchEncounterLabsQuery,
  useUploadEncounterPhotoMutation,
  useUpdateEncounterPhotosMutation,
  useCreateProcedureMutation,
  useUpdateProcedureMutation,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProcedureMutation,
  useFetchEncounterPatientsQuery,
  useLazyFetchEncounterPatientsQuery,
  useUpdatePatientContactMutation,
  useFetchPhysicianIntroductionsQuery,
  useLazyFetchPhysicianIntroductionsQuery,
  useCreatePhysicianIntroductionMutation,
  useCreateReferralSourceMutation,
  useFetchSoapNotesQuery,
  useSaveSoapNoteMutation,
  useDeleteSoapNoteMutation,
  useFetchSoapImagesQuery,
  useLazyFetchSoapImageQuery,
  useDeleteSoapImageMutation,
  useFetchESignFormQuery,
  useSubmitESignFormMutation,
  useFetchDocTemplateQuery,
  useSubmitDocTemplateMutation,
  useFetchSignedDocumentsQuery,
  useFetchIntakeTemplatesQuery,
  useFetchIntakeFormsQuery,
  useUploadPatientProfileImageMutation,
  useFetchEncounterCheckoutQuery,
  useFetchStripeTerminalsQuery,
  useLazyFetchStripeTerminalsQuery,
  usePayEncounterMutation,
  useCancelEncounterPayMutation,
  useSaveCreditCardMutation,
} = procedureApi
